import {Collapse} from "@mui/material";
import React from "react";

import {useTranslation} from "@i18n/client";
import Slide, {TransitionPosition} from "../../../animations/Slide";
import {CareEntryRowVariant, ExtendedCareEntrySearchResult} from "../../CareEntryRow";
import {TextInput} from "../../Input";
import {
  CareDiscoveryRowClickReporter,
  getSendClickedCareDiscoveryRowEvent,
} from "./careDiscoveryAnalytics";
import {getAllRelatedCares} from "./getAllRelatedCares";
import NoResults from "./NoResults";
import {useCareDiscoverySearchController} from "./useCareDiscoverySearchController";
import {useAreRecursiveTopicsEnabled} from "./useTopicStack";
import ResultList from "@components/Reason/CareDiscoverySearch/ResultList";
import {getBorder} from "@components/Reason/CareDiscoverySearch/ResultList/border";
import {useHoveredIndexController} from "../../../../hooks/useHoveredIndexController";
import useRelatedCares from "@components/Reason/CareDiscoverySearch/useRelatedCares";
import {ReasonForCareDiscoverySearch} from "./CareDiscoverySearch";

export type RenderTopicInputs = {
  care: ExtendedCareEntrySearchResult;
  result: ExtendedCareEntrySearchResult;
  index: number;
  reportClickEvent: CareDiscoveryRowClickReporter;
};

const TopicScreen = ({
  care,
  in: _in,
  careEntryRowVariant,
  isParentSearchActive,
  from,
  render,
  reasons,
}: {
  care: ExtendedCareEntrySearchResult;
  from: TransitionPosition;
  careEntryRowVariant: CareEntryRowVariant;
  in?: boolean;
  isParentSearchActive?: boolean;
  render: (inputs: RenderTopicInputs) => React.ReactNode;
  reasons: ReasonForCareDiscoverySearch[];
}) => {
  const {t} = useTranslation();
  const shouldShowSearch = useAreRecursiveTopicsEnabled() && getAllRelatedCares(care).length > 8;
  const careEntriesQuery = useRelatedCares(care);
  const {activeIndex, setHovered, unsetHovered} = useHoveredIndexController();

  const {
    isNoSymptomsSearched,
    handleQueryChange,
    searchCareEntriesQuery,
    showDefaultResults,
    showNoResults,
    resultsToRender,
  } = useCareDiscoverySearchController(careEntriesQuery.data, reasons);

  const loadingRowCount =
    showDefaultResults && careEntriesQuery.isLoading
      ? care.related_topics?.length ?? 0
      : searchCareEntriesQuery.isLoading
      ? 5
      : undefined;

  const reportClickEvent =
    isParentSearchActive || !showDefaultResults
      ? getSendClickedCareDiscoveryRowEvent("searched")
      : getSendClickedCareDiscoveryRowEvent("base");

  return (
    <Slide from={from} in={_in} duration={125} timeout={0} appear unmountOnExit>
      <div data-cy={`topic-expansion-${care.slug}`}>
        {shouldShowSearch && (
          <TextInput
            onChange={handleQueryChange}
            icon="search"
            placeholder={t("Search symptoms or services")}
            ariaLabel={t("Search symptoms or services")}
            data-cy={`topic-expansion-${care.slug}-discovery-search-input`}
            inputMode="text"
          />
        )}
        {showDefaultResults && (
          <span className="fs12 font-mr ttu gray600 pl-2 block pt-3">{care.name}</span>
        )}
        <Collapse in={showNoResults} unmountOnExit appear timeout={125}>
          <NoResults isNoSymptomsSearched={isNoSymptomsSearched} />
        </Collapse>
        {loadingRowCount ? (
          <ResultList.Loading
            reasons={reasons}
            loadingRowCount={loadingRowCount}
            keyPrefix={`topic-expansion-results-${care.slug}`}
            hideBorders
            rowVariant={careEntryRowVariant}
          />
        ) : (
          <ResultList className="mt-2 gap-1" onMouseLeave={unsetHovered}>
            {resultsToRender.map((result, i, arr) => {
              return (
                <ResultList.Item
                  className={getBorder(i, activeIndex, arr.length, true)}
                  key={`topic-expansion-results-${care.slug}-${i}-${result.name}`}
                  onMouseEnter={() => setHovered(i)}
                  transitionSpeed={0}
                >
                  {render({care, result, index: i, reportClickEvent})}
                </ResultList.Item>
              );
            })}
          </ResultList>
        )}
      </div>
    </Slide>
  );
};

export default React.memo(TopicScreen);
